/**
 * The limits and "prepare by hand" rules the API Worker's return model
 * (apps/api/src/federal-tax/return-model) applies and the customer app's
 * Summary mirrors (`hand-prepared.ts`, `mmllc-question4.ts`), so the two read
 * one number instead of two copies. The Worker stays the authority: it raises
 * the fill error; the app only tells the customer our team will prepare that
 * part by hand.
 */

// ---------------------------------------------------------------------------
// Corporation and partnership limits
// ---------------------------------------------------------------------------

/**
 * Section 448(c) gross receipts test for tax years beginning in 2025. Under it
 * a small business taxpayer need not capitalize costs under section 263A, so
 * Form 1125-A line 9e is "No"; at or above it the team prepares Form 1125-A
 * (i1120 2025 pages 6 and 11; i1065 2025, "Small business taxpayer").
 */
export const SMALL_BUSINESS_TAXPAYER_RECEIPTS = 31_000_000;

/**
 * Form 1065 Schedule B question 4 (i1065 2025 p. 26): "Yes" needs total
 * receipts under $250,000 and total assets under $1 million (and the K-1s on
 * time, no Schedule M-3).
 */
export const PARTNERSHIP_QUESTION4_RECEIPTS_LIMIT = 250_000;
export const PARTNERSHIP_QUESTION4_ASSETS_LIMIT = 1_000_000;

/**
 * Schedule M-3 replaces Schedule M-1 at $10 million of year-end total assets
 * (i1120 2025; i1065 2025 p. 18 "Item J"), or for a partnership also $35
 * million of total receipts. We do not prepare Schedule M-3.
 */
export const SCHEDULE_M3_ASSETS_LIMIT = 10_000_000;
export const PARTNERSHIP_M3_RECEIPTS_LIMIT = 35_000_000;

/**
 * Form 1120 Schedule K question 13: receipts and assets under $250,000 excuse
 * Schedules L, M-1 and M-2; at or above it the balance sheet is required.
 */
export const CORPORATION_SMALL_LIMIT = 250_000;

/** Form 1125-E (compensation of officers) is attached at $500,000 of total receipts (i1120 2025, line 12). */
export const OFFICER_COMPENSATION_RECEIPTS = 500_000;

/**
 * Form 1065 "Total receipts" for Schedule B question 4 (i1065 2025 p. 26):
 * line 1a plus the positive amounts of line 7 and Schedule K lines 5, 6a and 7
 * (the only ones of lines 4-7 and Schedule K lines 3a-11 the wizard asks).
 * Whole dollars in, whole dollars out.
 */
export function partnershipTotalReceipts(amounts: {
  grossReceipts: number;
  otherIncome: number;
  interest: number;
  dividends: number;
  royalties: number;
}): number {
  return (
    amounts.grossReceipts +
    Math.max(0, amounts.otherIncome) +
    Math.max(0, amounts.interest) +
    Math.max(0, amounts.dividends) +
    Math.max(0, amounts.royalties)
  );
}

/** Schedule B question 4's receipts and assets tests (the due date is the caller's). */
export function partnershipWithinQuestion4Limits(
  totalReceipts: number,
  totalAssets: number,
): boolean {
  return (
    totalReceipts < PARTNERSHIP_QUESTION4_RECEIPTS_LIMIT &&
    totalAssets < PARTNERSHIP_QUESTION4_ASSETS_LIMIT
  );
}

function answer(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

/**
 * A filed Form 1125-A the team prepares by hand: inventory valued with an
 * "other" method (line 9a(iii) needs an explanation), a change in counting or
 * valuing inventory (line 9f), or receipts at the section 263A limit (line
 * 9e). The Worker's `form1125aProblems` raises each one.
 */
export function form1125aNeedsHandPreparation(input: {
  inventoryMethod: unknown;
  inventoryChanged: unknown;
  totalReceipts: number;
}): boolean {
  return (
    answer(input.inventoryMethod) === "other" ||
    answer(input.inventoryChanged) === "yes" ||
    input.totalReceipts >= SMALL_BUSINESS_TAXPAYER_RECEIPTS
  );
}

// ---------------------------------------------------------------------------
// Multi-member LLC (Form 1065)
// ---------------------------------------------------------------------------

/**
 * Form 1065 lines that need a form, statement or allocation we do not fill:
 * any amount stops the build (the Worker's `unsupportedLines`). `field` is the
 * path under `financialData.form1065`; `needs` finishes "Line X needs ...".
 */
export const MMLLC_HAND_PREPARED_LINES = [
  {
    line: "16a",
    field: "deductions.depreciation",
    needs: "Form 4562 (depreciation)",
  },
  {
    line: "17",
    field: "deductions.depletion",
    needs: "the depletion statement",
  },
  {
    line: "K 9a",
    field: "scheduleK.capitalGainLoss",
    needs: "Schedule D (Form 1065)",
  },
  {
    line: "K 12",
    field: "adjustments.section179Deduction",
    needs: "Form 4562 (section 179)",
  },
  {
    line: "K 13a",
    field: "adjustments.charitableContributions",
    needs: "the contributions statement",
  },
  {
    line: "K 21",
    field: "adjustments.foreignTaxesPaid",
    needs: "Schedules K-2 and K-3 (foreign taxes)",
  },
] as const;

export type MmllcHandPreparedLine =
  (typeof MMLLC_HAND_PREPARED_LINES)[number]["line"];
export type MmllcHandPreparedField =
  (typeof MMLLC_HAND_PREPARED_LINES)[number]["field"];

/**
 * The hand-prepared lines with an amount, in the Worker's order. Each caller
 * reads the amounts its own way (whole dollars).
 */
export function mmllcHandPreparedLines(
  amounts: Readonly<Record<MmllcHandPreparedField, number>>,
): Array<(typeof MMLLC_HAND_PREPARED_LINES)[number]> {
  return MMLLC_HAND_PREPARED_LINES.filter((row) => amounts[row.field] !== 0);
}

/**
 * Yes/no answers on `financialData.form1065.details` whose "Yes" the team
 * prepares by hand (audit 2 item 24): Schedule B question 6 (canceled debt,
 * separately stated income), question 9 (a foreign trust, Form 3520) and K-1
 * item K1 (liabilities, shared by who bears the risk of loss).
 */
export const MMLLC_HAND_PREPARED_ANSWERS = {
  debtCanceled: "B 6",
  foreignTrust: "B 9",
  liabilities: "K-1 K1",
} as const;

export type MmllcHandPreparedAnswer = keyof typeof MMLLC_HAND_PREPARED_ANSWERS;

// ---------------------------------------------------------------------------
// Form 1040-NR (2025) limits the automatic return computes within
// ---------------------------------------------------------------------------

/**
 * Schedule A (Form 1040-NR) 2025, line 1 (state and local taxes): the cap, the
 * modified AGI where it starts to phase down, and the floor it phases down to
 * (i1040nr 2025). The Worker computes the cap only below the phase-down.
 */
export const SALT_LIMIT_2025 = { single: 40_000, mfs: 20_000 } as const;
export const SALT_PHASEDOWN_START_2025 = {
  single: 500_000,
  mfs: 250_000,
} as const;
export const SALT_FLOOR_2025 = { single: 10_000, mfs: 5_000 } as const;
/** Cash gifts above this share of 1040-NR line 11b need Pub. 526 (i1040nr 2025 p. 40). */
export const CASH_GIFT_SIMPLE_LIMIT = 0.3;
/** Form 8995 (simplified QBI deduction) applies at or below this taxable income; above it Form 8995-A (i8995 2025). */
export const QBI_SIMPLIFIED_THRESHOLD_2025 = 197_300;
